import { Descriptions as AntdDescriptions, Tag } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Descriptions from "../../general/antd/descriptions";
import PageDetails from "../../general/details";
import { useNavigate, useParams } from "react-router-dom";
import Spin from "../../general/antd/spin";
import AppContext from "../../../context/app/context";
import SkipReasonContext from "../../../context/skip-reason/context";
import SkipReasonContextProvider from "../../../context/skip-reason/provider";

interface Props {}

const SkipReasonDetailsComponent: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { direction } = useContext(AppContext);
  const { details, actions, loading } = useContext(SkipReasonContext);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const getDetails = async () => {
      id && (await actions.getDetails(Number(id)));
    };
    getDetails();
  }, [direction]);

  return (
    <>
      <PageDetails
        title={t("details")}
        subtitle={t("skip_reason")}
        buttonName={`${t("update_skip_reason")}`}
        onClick={() => navigate(`/skip-reasons/update/${id}`)}
        loading={loading.includes("details")}
        onRefresh={() => actions.getDetails(Number(id))}
      >
        {loading.includes("details") ? (
          <Spin />
        ) : (
          <Descriptions size="small" column={1} bordered>
            <AntdDescriptions.Item label={t("id")}>
              {details?.id}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("code")}>
              {details?.code ?? "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("ar_name")}>
              {details?.arName ?? "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("en_name")}>
              {details?.enName ?? "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("skip_type")}>
              {details?.isStoreSkip ? (
                <Tag icon={<CheckCircleOutlined />} color="blue">
                  {t("store_skip")}
                </Tag>
              ) : (
                <Tag icon={<CloseCircleOutlined />} color="orange">
                  {t("order_skip")}
                </Tag>
              )}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("status")}>
              {details?.isActive ? (
                <Tag icon={<CheckCircleOutlined />} color="success">
                  {t("active")}
                </Tag>
              ) : (
                <Tag icon={<CloseCircleOutlined />} color="default">
                  {t("inactive")}
                </Tag>
              )}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("created_at")}>
              {details?.createdAt
                ? new Date(details.createdAt).toLocaleString()
                : "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("created_by")}>
              {details?.createdBy
                ? `${details.createdBy.firstName ?? ""} ${details.createdBy.lastName ?? ""}`.trim() || "-"
                : "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("updated_at")}>
              {details?.updatedAt
                ? new Date(details.updatedAt).toLocaleString()
                : "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("updated_by")}>
              {details?.updatedBy
                ? `${details.updatedBy.firstName ?? ""} ${details.updatedBy.lastName ?? ""}`.trim() || "-"
                : "-"}
            </AntdDescriptions.Item>
          </Descriptions>
        )}
      </PageDetails>
    </>
  );
};

const SkipReasonDetailsPage = () => {
  return (
    <SkipReasonContextProvider>
      <SkipReasonDetailsComponent />
    </SkipReasonContextProvider>
  );
};

export default SkipReasonDetailsPage;
